"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.addAuthVars = void 0;
const graphql_1 = require("graphql");
const perms_1 = require("./config/perms");
const createIncludeDirective = (varName) => ({
    kind: "Directive",
    name: { kind: "Name", value: "include" },
    arguments: [
        {
            kind: "Argument",
            name: { kind: "Name", value: "if" },
            value: { kind: "Variable", name: { kind: "Name", value: varName } },
        },
    ],
});
const createVariable = (name) => ({
    kind: "VariableDefinition",
    variable: {
        kind: "Variable",
        name: { kind: "Name", value: name },
    },
    type: {
        kind: "NonNullType",
        type: {
            kind: "NamedType",
            name: { kind: "Name", value: "Boolean" },
        },
    },
    defaultValue: { kind: "BooleanValue", value: true },
});
const getEnclosingFragment = (ancestors) => {
    // NOTE: might not actually be these types, but testing the 'kind' field
    // will confirm it, as these are locked for each of the types
    const grandparent = ancestors?.[ancestors.length - 2];
    if (grandparent?.kind === "InlineFragment" ||
        grandparent?.kind === "FragmentDefinition") {
        return grandparent;
    }
    else {
        return null;
    }
};
/**
 * How to use:
 *
 * Restricted fields must be enclosed in fragments (either inline or named).
 *
 * Example, turn this:
 *
 * query {
 *   member {
 *     id
 *     accountNumber
 *   }
 * }
 *
 * Into this:
 *
 * query {
 *   member {
 *     id
 *     ... on member {
 *       accountNumber
 *     }
 *   }
 * }
 *
 * Wrap your gql tags with a call to this method:
 *
 * const query = addAuthVars(gql`
 *   query {
 *     member {
 *       id
 *       ... on member {
 *         accountNumber
 *       }
 *     }
 *   }
 * `);
 *
 * This will add vars to control fields that require auth.
 *
 * When calling this query, use auth.getQueryVars(query) to generate
 * the value of these vars to send with your normal input vars to the
 * GraphQL server
 *
 */
const addAuthVars = (query) => {
    const addedAuthVars = {};
    return (0, graphql_1.visit)(query, {
        OperationDefinition: {
            leave(node) {
                return {
                    ...node,
                    variableDefinitions: [
                        ...(node.variableDefinitions ?? []),
                        ...Object.values(addedAuthVars),
                    ],
                };
            },
        },
        Field(node, key, parent, path, ancestors) {
            // is this Field in a fragment with a 'restrict' directive?
            const fragment = getEnclosingFragment(ancestors);
            if (fragment && fragment.typeCondition) {
                const typeName = fragment.typeCondition.name.value;
                // does this field have roles applied?
                if (perms_1.QueryFieldPermissions[typeName]?.[node.name.value]) {
                    // add a var for controlling this field
                    const varName = `__auth__${typeName}__${node.name.value}`;
                    if (!addedAuthVars[varName]) {
                        addedAuthVars[varName] = createVariable(varName);
                    }
                    return {
                        ...node,
                        directives: [
                            ...(node.directives ?? []),
                            createIncludeDirective(varName),
                        ],
                    };
                }
            }
        },
    });
};
exports.addAuthVars = addAuthVars;
